import type {
    TResponseGetOperatingStatement,
    TAnnualizationTotalColumn,
    TAnnualizationPeriodColumn,
    TMonthYear,
} from "@/apis/odin/apiODIN";
import type { TMonth } from "./utils";
import {
    getHalfLabelWithMonths,
    getQuarterLabelWithMonths,
    getYearLabelWithMonths,
} from "./utils";
import type { TDtoTableExtractionAnnualized } from "./TDtoTableExtractionAnnualized";

type TOverlappingPeriods = {
    label: string;
    active: boolean;
    extractionId: number;
    cells: NonNullable<TResponseGetOperatingStatement>["rowCells"];
    rows: NonNullable<TResponseGetOperatingStatement>["rows"];
    duplication: boolean;
    columnIndex?: number;
    periodId: number;
    total: string;
    parentExtractionId?: number;
};

type TOperatingStatementColumnMeta =
    | {
          kind: "period";
          label: string;
          columnIndex: number;
          year: number;
          month: string | undefined;
          quarter: string | undefined;
          half: string | undefined;
          period: [TMonthYear, TMonthYear];
      }
    | {
          kind: "total";
          label: string;
          columnIndex: number;
          year: number;
          month: undefined;
          quarter: string | undefined;
          half: string | undefined;
      };

export const formatOperatingStatementLabel = ({
    year,
    month,
}: {
    year: number;
    month: number;
}): string => {
    const formattedMonth = formatOperatingStatementMonth(month);
    const formattedYear = formatOperatingStatementYear(year);

    return `${formattedMonth}/${formattedYear}`;
};

export const formatOperatingStatementPeriodLabel = (
    period: [TMonthYear, TMonthYear],
): string => {
    const [start, end] = period;
    const startLabel = formatOperatingStatementLabel({
        month: start.month,
        year: start.year,
    });
    const endLabel = formatOperatingStatementLabel({
        month: end.month,
        year: end.year,
    });

    if (start.month === end.month && start.year === end.year) return startLabel;
    return `${startLabel} - ${endLabel}`;
};

export const formatOperatingStatementMonth = (month: number): string => {
    const validMonth = Math.max(1, Math.min(12, month));
    return validMonth.toString().padStart(2, "0");
};

export const formatOperatingStatementYear = (year: number): string => {
    return (year % 100).toString().padStart(2, "0");
};

type TOperatingStatementColumn =
    NonNullable<TResponseGetOperatingStatement>["columns"][number];

const isMonthColumnTypeGuard = (
    column: TOperatingStatementColumn,
): column is TAnnualizationPeriodColumn =>
    Object.hasOwnProperty.call(column, "label") &&
    Object.hasOwnProperty.call(column, "columnOrder") &&
    Object.hasOwnProperty.call(column, "period") &&
    Object.hasOwnProperty.call(column, "columnType") &&
    Object.hasOwnProperty.call(column, "extractionId") &&
    Object.hasOwnProperty.call(column, "periodId") &&
    Object.hasOwnProperty.call(column, "overlappingColumns");

const isAnnualizationMonthPeriodColumnTypeGuard = (
    column: TOperatingStatementColumn,
): column is TAnnualizationPeriodColumn =>
    isMonthColumnTypeGuard(column) &&
    (column.columnType === "actual_month" ||
        column.columnType === "budget_month");

const isAnnualizationYearPeriodColumnTypeGuard = (
    column: TOperatingStatementColumn,
): column is TAnnualizationPeriodColumn =>
    isMonthColumnTypeGuard(column) &&
    (column.columnType === "actual_year" ||
        column.columnType === "budget_year" ||
        column.columnType === "budget_year_to_date" ||
        column.columnType === "trailing_year_to_date");

const isTotalColumnTypeGuard = (
    column: TOperatingStatementColumn,
): column is TAnnualizationTotalColumn =>
    Object.hasOwnProperty.call(column, "label") &&
    Object.hasOwnProperty.call(column, "columnOrder") &&
    Object.hasOwnProperty.call(column, "monthStart") &&
    Object.hasOwnProperty.call(column, "monthEnd") &&
    Object.hasOwnProperty.call(column, "includedColumnLabels");

export const convertAPIResponseToDTOTableExtractionAnnualized = (
    apiResponse: TResponseGetOperatingStatement | null,
    group: "month" | "quarter" | "half" | "year",
): TDtoTableExtractionAnnualized | null => {
    if (!apiResponse) return null;

    const periodColumns = apiResponse.columns.filter(isMonthColumnTypeGuard);
    const activePeriodColumns =
        group === "month"
            ? periodColumns.filter(isAnnualizationMonthPeriodColumnTypeGuard)
            : group === "year"
              ? periodColumns.filter(isAnnualizationYearPeriodColumnTypeGuard)
              : [];
    const totalColumns = apiResponse.columns.filter(isTotalColumnTypeGuard);
    let overlappingPeriodsColumns: TOverlappingPeriods[] = [];
    let firstMonthColumn: TMonth | null = null;
    let lastMonthColumn: TMonth | null = null;
    let emptyMonths: TMonth[] = [];
    apiResponse.columns.forEach(c => {
        if (isMonthColumnTypeGuard(c)) {
            if (c.columnOrder === 0) {
                firstMonthColumn = c.period[0];
            }
            if (c.columnOrder === apiResponse.columns.length - 1) {
                lastMonthColumn = c.period[1];
            }
            if (c.empty) {
                if (
                    !emptyMonths.some(
                        m =>
                            m.month === c.period[0].month &&
                            m.year === c.period[0].year,
                    )
                ) {
                    emptyMonths = [...emptyMonths, c.period[0]];
                }
            }
        } else if (isTotalColumnTypeGuard(c)) {
            c.emptyMonths?.forEach(month => {
                if (
                    !emptyMonths.some(
                        m => m.month === month.month && m.year === month.year,
                    )
                ) {
                    emptyMonths = [...emptyMonths, month];
                }
            });
            if (c.columnOrder === 0 && !firstMonthColumn) {
                firstMonthColumn = c.monthStart;
            }
            if (
                c.columnOrder === apiResponse.columns.length - 1 &&
                !lastMonthColumn
            ) {
                lastMonthColumn = c.monthEnd;
            }
        }
    });
    activePeriodColumns.forEach(c => {
        const label = formatOperatingStatementPeriodLabel(c.period);

        c.overlappingColumns.forEach(column => {
            // TODO: temporarily skip duplicated columns with same values
            if (column.duplication) return;
            overlappingPeriodsColumns = [
                ...overlappingPeriodsColumns,
                {
                    label,
                    extractionId: column.extractionId,
                    active: column.active,
                    cells: [column.cells],
                    rows: column.rows,
                    duplication: column.duplication,
                    periodId: c.periodId,
                    total: column.total,
                    parentExtractionId: c.extractionId,
                },
            ];
        });
        if (c.overlappingColumns.length === 0) {
            overlappingPeriodsColumns = [
                ...overlappingPeriodsColumns,
                {
                    label,
                    extractionId: c.extractionId,
                    active: true,
                    cells: [],
                    rows: [],
                    duplication: false,
                    periodId: c.periodId,
                    total: "",
                    parentExtractionId: c.extractionId,
                },
            ];
        }
    });

    const operatingStatementsPeriods = activePeriodColumns.map(periodColumn => {
        const label = formatOperatingStatementPeriodLabel(periodColumn.period);
        const isMonth =
            periodColumn.columnType === "actual_month" ||
            periodColumn.columnType === "budget_month";
        const month = isMonth
            ? formatOperatingStatementMonth(periodColumn.period[0].month)
            : undefined;
        const quarter = isMonth
            ? `Q${Math.ceil(periodColumn.period[0].month / 3)}`
            : undefined;
        const half = isMonth
            ? `H${Math.ceil(periodColumn.period[0].month / 6)}`
            : undefined;

        const out: TOperatingStatementColumnMeta = {
            kind: "period",
            label,
            columnIndex: periodColumn.columnOrder,
            year: periodColumn.period[1].year,
            month,
            quarter,
            half,
            period: periodColumn.period,
        };
        return out;
    });
    const operatingStatementsTotals = totalColumns.reduce(
        (acc, totalColumn) => {
            const monthStart = formatOperatingStatementMonth(
                totalColumn.monthStart.month,
            );
            const monthEnd = formatOperatingStatementMonth(
                totalColumn.monthEnd.month,
            );
            const isQuarter = totalColumn.period === 3;
            const isHalf = totalColumn.period === 6;
            const unique = acc.addedColumns.has(
                `${monthStart} - ${monthEnd}/${totalColumn.monthEnd.year}`,
            );
            const quarter = `Q${Math.ceil(totalColumn.monthEnd.month / 3)}`;
            acc.addedColumns.add(
                `${monthStart} - ${monthEnd}/${totalColumn.monthEnd.year}`,
            );
            const half = `H${Math.ceil(totalColumn.monthEnd.month / 6)}`;
            return {
                cols: [
                    ...acc.cols,
                    {
                        kind: "total" as const,
                        label: `${monthStart} - ${monthEnd}`,
                        columnIndex: totalColumn.columnOrder,
                        year: totalColumn.monthEnd.year,
                        month: undefined,
                        quarter: isQuarter && !unique ? quarter : undefined,
                        half:
                            (isHalf || isQuarter) && !unique ? half : undefined,
                    },
                ],
                addedColumns: acc.addedColumns,
            };
        },
        {
            cols: [] as Extract<
                TOperatingStatementColumnMeta,
                { kind: "total" }
            >[],
            addedColumns: new Set(),
        },
    );

    const operatingStatementsMonthsColumnsWithTotal: TOperatingStatementColumnMeta[] =
        [...operatingStatementsPeriods, ...operatingStatementsTotals.cols];

    const operatingStatementsMonthsColumnsWithTotalSorted =
        operatingStatementsMonthsColumnsWithTotal.toSorted(
            (a, b) => a.columnIndex - b.columnIndex,
        );

    const columnDefsOperatingStatements =
        operatingStatementsMonthsColumnsWithTotalSorted.map(column => {
            let children: Exclude<
                TDtoTableExtractionAnnualized["columnDefsOperatingStatements"][string][string][number]["children"],
                undefined
            > = [];
            let extractionId: number | undefined = undefined;
            if (column.kind === "period") {
                const matches = overlappingPeriodsColumns.filter(
                    c => c.label === column.label,
                );

                if (matches.length > 0) {
                    matches.forEach(match => {
                        if (match.extractionId === match.parentExtractionId) {
                            return;
                        }
                        children = [
                            ...children,
                            {
                                headerName: column.month ?? column.label,
                                field: `operating-statement-month-${column.columnIndex}-${match.extractionId}`,
                                extractionId: match.extractionId,
                            },
                        ];
                        match.columnIndex = column.columnIndex;
                    });
                }
                extractionId = matches[0]?.parentExtractionId;
            }
            return {
                headerName:
                    column.month !== undefined ? column.month : column.label,
                field:
                    column.kind === "period"
                        ? `operating-statement-month-${column.columnIndex}`
                        : `operating-statement-total-column-${column.columnIndex}`,
                children: children.length > 0 ? children : undefined,
                extractionId,
                year: column.year,
                month: column.month,
                quarter: column.quarter,
                half: column.half,
            };
        });
    const reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues = (
        rowId: number,
    ) =>
        operatingStatementsMonthsColumnsWithTotal.reduce(
            (acc, { columnIndex }) => {
                const currentColumn = apiResponse.columns.find(
                    monthColumn => monthColumn.columnOrder === columnIndex,
                );
                const currentColumnCells = apiResponse.rowCells.find(
                    cell =>
                        parseInt(cell["col_identifier"] ?? "0") ===
                        currentColumn?.columnOrder,
                );
                if (currentColumn === undefined) {
                    return acc;
                }
                const fieldId = isMonthColumnTypeGuard(currentColumn)
                    ? `operating-statement-month-${columnIndex}`
                    : `operating-statement-total-column-${columnIndex}`;

                const fieldValue = currentColumnCells?.[rowId] ?? null;
                const hasValueThatCanBeCastedToNumber =
                    fieldValue !== null &&
                    !isNaN(Number(fieldValue)) &&
                    isFinite(Number(fieldValue));

                if (hasValueThatCanBeCastedToNumber) {
                    const fieldValueWithoutCommasAndDotsAndSpaces =
                        fieldValue.replace(/[.,\s]/g, "");
                    const fieldValueAsInteger = parseInt(
                        fieldValueWithoutCommasAndDotsAndSpaces,
                        10,
                    );
                    acc[fieldId] = fieldValueAsInteger;
                } else {
                    acc[fieldId] = fieldValue;
                }

                return acc;
            },
            {} as Record<string, string | number | null>,
        );

    const reduceOverlappingPeriodsToRows: (
        rowId: number,
    ) => Record<string, string | number | null> | null = (rowId: number) =>
        overlappingPeriodsColumns.reduce(
            (acc, { columnIndex, extractionId, cells }) => {
                if (columnIndex === undefined) return acc;
                const fieldKey = `operating-statement-month-${columnIndex}-${extractionId}`;
                const fieldValue: string | undefined =
                    cells[0]?.[rowId] ?? undefined;
                const hasValueThatCanBeCastedToNumber =
                    fieldValue !== null &&
                    !isNaN(Number(fieldValue)) &&
                    isFinite(Number(fieldValue));
                if (hasValueThatCanBeCastedToNumber) {
                    const fieldValueWithoutCommasAndDotsAndSpaces: string = (
                        fieldValue as string
                    ).replace(/[.,\s]/g, "");
                    const fieldValueAsInteger = parseInt(
                        fieldValueWithoutCommasAndDotsAndSpaces,
                        10,
                    );
                    acc[fieldKey] = fieldValueAsInteger;
                } else {
                    acc[fieldKey] = fieldValue ?? null;
                }
                return acc;
            },
            {} as Record<string, string | number | null>,
        );

    const buildStructuredRows = (
        sourceRows: typeof apiResponse.rows,
    ): TDtoTableExtractionAnnualized["rows"] => {
        let permanentRows: TDtoTableExtractionAnnualized["rows"] = [];
        let sectionBuffer: TDtoTableExtractionAnnualized["rows"] = [];
        let sectionName: string | null = null;
        let subcategoryRowId: number | null = null;

        const flush = () => {
            if (sectionName === null && sectionBuffer.length === 0) return;
            const sectionHeader: TDtoTableExtractionAnnualized["rows"] =
                sectionName !== null && sectionName.trim() !== ""
                    ? [
                          {
                              rowId: sectionName,
                              account: sectionName,
                              type: "HEADER",
                              isEmpty: false,
                          },
                      ]
                    : [];
            permanentRows = [
                ...permanentRows,
                ...sectionHeader,
                ...sectionBuffer,
            ];
            sectionBuffer = [];
            sectionName = null;
        };

        sourceRows.forEach(row => {
            if (row.rowType === "SECTION") {
                subcategoryRowId = null;
                sectionName = row.name;
                const sectionTotalRow: TDtoTableExtractionAnnualized["rows"] =
                    row.name.trim() === ""
                        ? []
                        : [
                              {
                                  rowId: row.rowOrder,
                                  account: `Total ${row.name}`,
                                  type: row.rowType,
                                  isEmpty: row.isEmpty,
                                  ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                                      row.rowOrder,
                                  ),
                                  ...reduceOverlappingPeriodsToRows(
                                      row.rowOrder,
                                  ),
                              },
                          ];
                sectionBuffer = [...sectionBuffer, ...sectionTotalRow];
                flush();
            } else if (row.rowType === "TYPE") {
                subcategoryRowId = null;
                flush();
                permanentRows = [
                    ...permanentRows,
                    {
                        rowId: row.rowOrder,
                        account: row.name,
                        type: row.rowType,
                        isEmpty: row.isEmpty,
                        ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                            row.rowOrder,
                        ),
                        ...reduceOverlappingPeriodsToRows(row.rowOrder),
                    },
                ];
            } else {
                const isNestedAccount =
                    row.rowType === "ACCOUNT" && row.depth === 1;
                if (row.rowType === "SUBSECTION") {
                    subcategoryRowId = row.rowOrder;
                } else if (!isNestedAccount) {
                    subcategoryRowId = null;
                }
                const parentRowIdField: Record<string, number | null> =
                    isNestedAccount ? { parentRowId: subcategoryRowId } : {};
                sectionBuffer = [
                    ...sectionBuffer,
                    {
                        rowId: row.rowOrder,
                        account: row.name,
                        type: row.rowType,
                        isEmpty: row.isEmpty,
                        ...parentRowIdField,
                        ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                            row.rowOrder,
                        ),
                        ...reduceOverlappingPeriodsToRows(row.rowOrder),
                    },
                ];
            }
        });
        flush();
        return permanentRows;
    };

    const nonOperatingRows = buildStructuredRows(
        apiResponse.rows.filter(row => row.isNonOperating),
    );
    const rowsPermanentWithOperatingStatements = buildStructuredRows(
        apiResponse.rows.filter(row => !row.isNonOperating),
    );

    const groupedColumnDefs: TDtoTableExtractionAnnualized["columnDefsOperatingStatements"] =
        columnDefsOperatingStatements.reduce(
            (acc, column) => {
                const year = column.year;
                const yearString = column.year?.toString() ?? "undefined";
                const quarterString = column.quarter ?? "Q1-Q4";
                const halfString = column.half ?? "H1-H2";

                const yearLabelWithMonths =
                    group === "year"
                        ? getYearLabelWithMonths({
                              firstMonthColumn,
                              lastMonthColumn,
                              year,
                              emptyMonths,
                          })
                        : yearString;

                const quarterLabelWithMonths =
                    group === "quarter" && quarterString !== "Q1-Q4"
                        ? getQuarterLabelWithMonths({
                              firstMonthColumn,
                              lastMonthColumn,
                              year,
                              quarterString,
                              emptyMonths,
                          })
                        : quarterString;

                const halfLabelWithMonths =
                    group === "half" && halfString !== "H1-H2"
                        ? getHalfLabelWithMonths({
                              firstMonthColumn,
                              lastMonthColumn,
                              year,
                              halfString,
                              emptyMonths,
                          })
                        : halfString;

                if (!acc[yearLabelWithMonths]) {
                    acc[yearLabelWithMonths] = {};
                }
                if (group !== "half") {
                    if (!acc[yearLabelWithMonths][quarterLabelWithMonths]) {
                        acc[yearLabelWithMonths][quarterLabelWithMonths] = [];
                    }
                    if (quarterLabelWithMonths === "Q1-Q4") {
                        acc[yearLabelWithMonths][quarterLabelWithMonths] = [
                            column,
                        ];
                    } else {
                        acc[yearLabelWithMonths][quarterLabelWithMonths] = [
                            ...acc[yearLabelWithMonths][quarterLabelWithMonths],
                            column,
                        ];
                    }
                } else {
                    if (!acc[yearLabelWithMonths][halfLabelWithMonths]) {
                        acc[yearLabelWithMonths][halfLabelWithMonths] = [];
                    }
                    acc[yearLabelWithMonths][halfLabelWithMonths] = [
                        ...acc[yearLabelWithMonths][halfLabelWithMonths],
                        column,
                    ];
                }

                return acc;
            },
            {} as Record<
                string,
                Record<string, typeof columnDefsOperatingStatements>
            >,
        );

    const totalNetIncome = apiResponse.netOperatingIncomeCells.reduce(
        (acc, cell) => {
            if (group !== "month" && !cell.isTotal) {
                return acc;
            }
            const columnIndex = cell.columnOrder;
            const fieldValue = cell.total;

            // TODO: should add extractionId to all period columns
            const fieldId =
                cell.extractionId !== undefined && cell.extractionId !== null
                    ? cell.isActive
                        ? `operating-statement-month-${columnIndex}`
                        : `operating-statement-month-${columnIndex}-${cell.extractionId}`
                    : `operating-statement-total-column-${columnIndex}`;

            const hasValueThatCanBeCastedToNumber =
                fieldValue !== null &&
                !isNaN(Number(fieldValue)) &&
                isFinite(Number(fieldValue));

            if (hasValueThatCanBeCastedToNumber) {
                const fieldValueWithoutCommasAndDotsAndSpaces =
                    fieldValue.replace(/[.,\s]/g, "");
                const fieldValueAsInteger = parseInt(
                    fieldValueWithoutCommasAndDotsAndSpaces,
                    10,
                );
                acc[fieldId] = fieldValueAsInteger;
            } else {
                acc[fieldId] = 0;
            }
            return acc;
        },
        {} as Record<string, number>,
    );

    return {
        rows: rowsPermanentWithOperatingStatements,
        nonOperatingRows,
        totalNetIncome,
        columnDefsOperatingStatements: groupedColumnDefs,
    };
};
